import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CANAIS_COMISSAO,
  ROTULO_CANAL_COMISSAO,
  ROTULO_TIPO_REGRA_COMISSAO,
  TIPOS_REGRA_COMISSAO,
  formatarReais,
  type CanalComissao,
  type StatusComissao,
  type TipoRegraComissao,
} from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi } from '../api/cliente.ts';
import { CampoCaixa, CampoSelecao, CampoTexto } from '../componentes/Campo.tsx';
import { Dialogo } from '../componentes/Dialogo.tsx';
import { RecusaDaApi } from '../componentes/RecusaDaApi.tsx';
import { useOperadoras } from '../dominio/operadoras.ts';
import { Aviso, CabecalhoDePagina, Botao, Cartao, Celula, Indicador, Linha, LinhaDeLista, Pilula, Tabela, data, } from '../componentes/Superficie.tsx';

type Comissao = {
  id: string;
  contato: { id: string; nome: string };
  operadora: string | null;
  canal: CanalComissao;
  descricao: string;
  competencia: string;
  valorCentavos: number;
  recorrente: boolean;
  status: StatusComissao;
  recebidaEm: string | null;
};

type Regra = {
  id: string;
  operadora: string;
  canal: CanalComissao;
  tipo: TipoRegraComissao;
  percentual: number;
  parcelas: number;
  vitalicio: number;
  ativa: boolean;
};

type Painel = {
  previstoCentavos: number;
  recebidoCentavos: number;
  porCanal: Array<{ canal: CanalComissao; previstoCentavos: number; recebidoCentavos: number }>;
  cronograma: Comissao[];
  regras: Regra[];
};

const FILTROS = [
  { valor: 'TODAS', rotulo: 'Todas' },
  { valor: 'PREVISTA', rotulo: 'Previstas' },
  { valor: 'RECEBIDA', rotulo: 'Recebidas' },
] as const;

/**
 * Comissões da Reducee.
 *
 * Receita da corretora, não da vendedora — a bonificação de cada uma fica em
 * Metas. Nenhuma linha é criada à mão: o cronograma nasce do pagamento do
 * primeiro boleto, e a tela só confirma o que de fato entrou.
 */
export function PaginaComissoes(): React.JSX.Element {
  const [filtro, definirFiltro] = useState<(typeof FILTROS)[number]['valor']>('TODAS');
  const [cadastrandoRegra, definirCadastrandoRegra] = useState(false);
  const clienteDeConsultas = useQueryClient();

  const consulta = useQuery({
    queryKey: ['comissoes', filtro],
    queryFn: () => chamarApi<Painel>(`/comissoes?filtro=${filtro}`),
  });

  const alternar = useMutation({
    mutationFn: (id: string) => chamarApi(`/comissoes/${id}/recebimento`, { metodo: 'POST' }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['comissoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const painel = consulta.data;

  return (
    <section>
      <CabecalhoDePagina
        area="Receita comercial"
        titulo="Comissões da Reducee"
        descricao="Direto pela operadora, LUHE ou Plataforma Vida."
        acoes={
          <Botao variante="primario" onClick={() => definirCadastrandoRegra(true)}>
            + Regra de comissão
          </Botao>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador rotulo="Previsto" valor={painel ? formatarReais(painel.previstoCentavos) : '—'} />
        <Indicador
          rotulo="Recebido"
          valor={painel ? formatarReais(painel.recebidoCentavos) : '—'}
          severidade="sucesso"
        />
        {(painel?.porCanal ?? []).map((canal) => (
          <Indicador
            key={canal.canal}
            rotulo={ROTULO_CANAL_COMISSAO[canal.canal]}
            valor={formatarReais(canal.previstoCentavos)}
            detalhe={`${formatarReais(canal.recebidoCentavos)} recebida`}
          />
        ))}
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
      <Cartao
        titulo="Regras cadastradas"
        descricao="Regra padrão por operadora + canal. Cada venda guarda a regra usada no fechamento."
      >
        {painel && painel.regras.length > 0 ? (
          painel.regras.map((regra) => (
            <LinhaDeLista
              key={regra.id}
              titulo={`${regra.operadora} · ${ROTULO_CANAL_COMISSAO[regra.canal]}`}
              detalhe={
                regra.tipo === 'PARCELA_UNICA'
                  ? `${regra.percentual}% · parcela única`
                  : `${regra.parcelas} × ${regra.percentual}% + ${regra.vitalicio}% vitalício`
              }
              valor={<Pilula tom={regra.ativa ? 'sucesso' : 'neutro'}>
                {regra.ativa ? 'Ativa' : 'Inativa'}
              </Pilula>}
            />
          ))
        ) : (
          <p className="py-4.5 text-xs text-texto-suave">Nenhuma regra cadastrada.</p>
        )}
      </Cartao>

      <Cartao titulo="Como o cálculo funciona">
        <Aviso>
          <b>Direto:</b> 3 parcelas de 100% do valor-base + 2% vitalício a partir da 4ª parcela.
          <br />
          <br />
          <b>LUHE / Plataforma Vida:</b> Bradesco 340% em parcela única; SulAmérica 270% em parcela
          única.
        </Aviso>
      </Cartao>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTROS.map((opcao) => (
          <Botao
            key={opcao.valor}
            variante={filtro === opcao.valor ? 'primario' : 'secundario'}
            onClick={() => definirFiltro(opcao.valor)}
          >
            {opcao.rotulo}
          </Botao>
        ))}
      </div>

      <Cartao
        className="mt-3"
        titulo="Agenda de comissões"
        descricao="Parcelas previstas geradas após o pagamento do primeiro boleto."
      >
        <Tabela
          colunas={[
            'Previsão',
            'Cliente',
            'Operadora',
            'Canal',
            'Parcela',
            'Valor',
            'Status',
            'Ação',
          ]}
          vazio="Nenhuma comissão gerada. Ela nasce após a confirmação do primeiro boleto."
          carregando={consulta.isLoading}
        >
          {(painel?.cronograma ?? []).map((comissao) => (
            <Linha key={comissao.id}>
              <Celula>{data(comissao.competencia)}</Celula>
              <Celula className="font-medium">{comissao.contato.nome}</Celula>
              <Celula>{comissao.operadora ?? '—'}</Celula>
              <Celula>{ROTULO_CANAL_COMISSAO[comissao.canal]}</Celula>
              <Celula>{comissao.descricao}</Celula>
              <Celula className="font-semibold">{formatarReais(comissao.valorCentavos)}</Celula>
              <Celula>
                <Pilula tom={comissao.status === 'RECEBIDA' ? 'sucesso' : 'alerta'}>
                  {comissao.status === 'RECEBIDA'
                    ? `Recebida ${data(comissao.recebidaEm)}`
                    : 'Prevista'}
                </Pilula>
              </Celula>
              <Celula>
                <Botao
                  tamanho="mini"
                  onClick={() => alternar.mutate(comissao.id)}
                  desabilitado={alternar.isPending}
                >
                  {comissao.status === 'RECEBIDA' ? 'Desfazer' : 'Marcar recebida'}
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Cartao>

      {cadastrandoRegra ? (
        <JanelaDaRegraDeComissao aoFechar={() => definirCadastrandoRegra(false)} />
      ) : null}
    </section>
  );
}

/**
 * A janela "Nova regra" do protótipo.
 *
 * Nenhuma linha do cronograma é criada à mão, mas a regra que o gera sim: sem
 * ela, a venda de uma operadora nova nasce sem comissão prevista e some do
 * placar. O `PUT /comissoes/regras` já existia e nenhuma tela o alcançava.
 *
 * A gravação é por operadora + canal: salvar a mesma dupla duas vezes corrige
 * a regra em vez de duplicá-la.
 */
function JanelaDaRegraDeComissao({ aoFechar }: { aoFechar: () => void }): React.JSX.Element {
  const operadoras = useOperadoras();
  const clienteDeConsultas = useQueryClient();

  /**
   * Controlado porque decide o resto da janela: em parcela única a API grava
   * uma parcela e zera o vitalício, e deixar os dois campos abertos prometeria
   * um cadastro que não seria gravado.
   */
  const [tipo, definirTipo] = useState<TipoRegraComissao>(TIPOS_REGRA_COMISSAO[0]);

  /**
   * Parcelas e vitalício também são controlados, e não lidos do `FormData`:
   * campo desabilitado não entra no `FormData`, e em parcela única os dois
   * chegariam zerados à API, que recusa parcela zero. Controlados, a janela
   * mostra exatamente o que vai ser gravado.
   */
  const [parcelas, definirParcelas] = useState('3');
  const [vitalicio, definirVitalicio] = useState('2');

  const parcelaUnica = tipo === 'PARCELA_UNICA';

  const salvar = useMutation({
    mutationFn: (campos: FormData) =>
      chamarApi('/comissoes/regras', {
        metodo: 'PUT',
        corpo: {
          operadora: String(campos.get('operadora') ?? '').trim(),
          canal: String(campos.get('canal')) as CanalComissao,
          tipo,
          percentual: Number(campos.get('percentual')),
          parcelas: parcelaUnica ? 1 : Number(parcelas),
          vitalicio: parcelaUnica ? 0 : Number(vitalicio),
          ativa: campos.get('ativa') === 'on',
        },
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['comissoes'] });
      aoFechar();
    },
  });

  return (
    <Dialogo area="Comissionamento" titulo="Nova regra" aoFechar={aoFechar}>
      <form
        data-teste="formulario-regra-comissao"
        onSubmit={(evento) => {
          evento.preventDefault();
          salvar.mutate(new FormData(evento.currentTarget));
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          {/* Da lista do cadastro, e não texto livre: a regra casa a venda pelo
            * nome, e "Hapvida " com espaço deixou venda sem comissão (NEX-204).
            * Operadora nova se cadastra antes, em Configurações (NEX-228). */}
          <CampoSelecao
            key={operadoras.pronta ? 'operadoras-prontas' : 'operadoras-carregando'}
            rotulo="Operadora"
            name="operadora"
            required
            defaultValue=""
            dica={
              operadoras.pronta && operadoras.nomes.length === 0
                ? 'Nenhuma operadora ativa. Cadastre em Configurações → Operadoras.'
                : undefined
            }
          >
            <option value="" disabled>
              Selecione
            </option>
            {operadoras.nomes.map((nome) => (
              <option key={nome} value={nome}>
                {nome}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao rotulo="Canal" name="canal" defaultValue={CANAIS_COMISSAO[0]}>
            {CANAIS_COMISSAO.map((canal) => (
              <option key={canal} value={canal}>
                {ROTULO_CANAL_COMISSAO[canal]}
              </option>
            ))}
          </CampoSelecao>

          <CampoSelecao
            rotulo="Tipo"
            className="md:col-span-2"
            value={tipo}
            onChange={(evento) => definirTipo(evento.target.value as TipoRegraComissao)}
          >
            {TIPOS_REGRA_COMISSAO.map((opcao) => (
              <option key={opcao} value={opcao}>
                {ROTULO_TIPO_REGRA_COMISSAO[opcao]}
              </option>
            ))}
          </CampoSelecao>

          <CampoTexto
            rotulo="Percentual inicial (%)"
            name="percentual"
            type="number"
            step="0.01"
            min="0"
            max="1000"
            required
            defaultValue="100"
            dica="Percentual do valor-base pago em cada parcela inicial."
          />

          <CampoTexto
            rotulo="Parcelas iniciais"
            type="number"
            min="1"
            max="36"
            required
            disabled={parcelaUnica}
            value={parcelaUnica ? '1' : parcelas}
            onChange={(evento) => definirParcelas(evento.target.value)}
            dica={parcelaUnica ? 'Parcela única grava sempre uma parcela.' : undefined}
          />

          <CampoTexto
            rotulo="Vitalício (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            required
            disabled={parcelaUnica}
            value={parcelaUnica ? '0' : vitalicio}
            onChange={(evento) => definirVitalicio(evento.target.value)}
            dica={parcelaUnica ? 'Parcela única não tem vitalício.' : 'A partir da parcela seguinte.'}
          />

          <CampoCaixa rotulo="Regra ativa" name="ativa" defaultChecked className="md:pt-5" />
        </div>

        <RecusaDaApi erro={salvar.error} padrao="Não foi possível salvar a regra." />

        <div className="mt-4 flex justify-end gap-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao tipo="submit" variante="primario" desabilitado={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar regra'}
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}
